import { ComponentInstance, NodeRedFlow } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import generateDeterministicUuid from '../../../utilities/generateDeterministicUuid';
import generateDeterministicNodeId from '../../../utilities/generateDeterministicNodeId';
import { ComponentTemplate } from '../types';

/**
 * Re-generates new unique IDs for each component and logic node in the given
 * component and the set of logic flows. It returns new versions of those where existing IDs
 * are replaced with new unique ones, preserving functionality.
 */
export default function regenerateUniqueComponentInstanceIds(
  template: ComponentTemplate,
  rootId: string,
  pageId: number,
): ComponentTemplate {
  const { component, flows } = template;
  // NOTE: This implementation is copied from `instantiateComponentJSON` from `ComponentUtils` AngularJS directive!
  // It does a very naive string replacement for IDs in JSON-serialized string.
  const replacements: [string, string][] = [[component.id, rootId]];

  const addComponentIdReplacements = (comps: ComponentInstance[]): void => {
    comps.forEach((comp) => {
      const oldId = comp.id;
      let newId = generateDeterministicUuid([String(pageId), rootId, oldId]);
      const parts = oldId.split('-');
      const lastPart = parts.pop();
      const newIdParts = newId.split('-');
      newIdParts.pop();
      newId = `${newIdParts.join('-')}-${lastPart}`;
      replacements.push([oldId, newId]);
      addComponentIdReplacements(comp.children);
    });
  };

  addComponentIdReplacements(component.children);

  const addFlowIdToMap = (flow: NodeRedFlow) => {
    const oldId = flow.id;
    if (oldId.indexOf('page:') === 0) {
      return;
    }
    const [, lastPart] = oldId.split('.');
    const [firstPart] = generateDeterministicNodeId([String(pageId), rootId, oldId]).split('.');
    const newId = `${firstPart}.${lastPart}`;
    replacements.push([oldId, newId]);
  };

  flows.forEach(addFlowIdToMap);

  replacements.push(['page[:][0-9]+[:]component', `page:${pageId}:component`]);

  // Horribly JSON-serialize + replace + parse
  let templateJson = JSON.stringify(template);

  replacements.forEach(([oldIdPattern, newId]) => {
    const regexp = new RegExp(oldIdPattern, 'g');
    templateJson = templateJson.replace(regexp, newId);
  });

  return JSON.parse(templateJson);
}
